// Wire the per-frame render loop. The startLoop helper in @render/loop owns
// the requestAnimationFrame chain; this phase just hands it the camera,
// scene, controls, and the per-frame side effects (keyboard input, coord
// overlay update).

import { params } from '@store/instance';
import { threeJSToPhysical } from '@adapters/coordinates';
import { handleInput as handleKeyboardInput } from '@input/keyboard';
import { navModeUsesOrbitControls } from '../cameraModes';
import { startLoop } from '@render/loop';
import { updateMarker, tickSelectionTooltip, applyHandleOrientation } from '@scene/picking';
import { updateCoordinateCameraDetails } from '@ui/coordinateDisplay';
import type { ViewerContext } from '../context';

export function startAnimateLoop(viewer: ViewerContext): void {
    if (viewer.loop.handle) return;
    const camera = viewer.three.camera;
    const renderer = viewer.three.renderer;
    const scene = viewer.three.scene;
    const controls = viewer.three.controls;
    if (!camera || !renderer || !scene || !controls) return;
    // Camera + controls are read live each frame (a projection swap recreates
    // both); the per-frame closures below read viewer.three.camera live too.
    viewer.loop.handle = startLoop({
        stats: viewer.three.stats,
        clock: viewer.three.clock,
        getCamera: () => viewer.three.camera,
        getControls: () => viewer.three.controls,
        scene,
        renderer,
        rotatingObjects: viewer.objects.rotating,
        gridLabels: viewer.objects.axes.labels,
        coordLabels: viewer.objects.axes.coordLabels,
        updateMarker,
        tickHandleOrientation: () => { if (viewer.three.camera) applyHandleOrientation(viewer.three.camera); },
        tickSelectionTooltip: () => { if (viewer.three.camera) tickSelectionTooltip(viewer.three.camera, renderer); },
        handleKeyboardInput: (dt) => handleKeyboardInput(viewer, params, dt),
        shouldUpdateControls: navModeUsesOrbitControls,
        updateCoordinateCameraDetails: () => { if (viewer.three.camera) updateCoordinateCameraDetails({
            camera: viewer.three.camera, threeJSToPhysical,
            units: viewer.sceneMeta.units,
        }); },
        // Per-frame tick for the orientation gnomon. The cameraGnomon
        // install module assigns the closure once it has its tiny
        // dedicated renderer wired up; null until then.
        onAfterRender: (dt) => { viewer.three.viewHelperTick?.(dt); },
    });
}
